import { getDomainHistory, getWatchlist, removeFromWatchlist, addToWatchlist } from "../api/domainMonitor";
import type { DomainHistoryItem, WatchlistItem } from "../types/domainMonitor";
import { toast } from "sonner";
import React from "react";

type Listener = () => void;

class DomainStore {
  public history: DomainHistoryItem[] = [];
  public watchlist: WatchlistItem[] = [];
  private listeners: Set<Listener> = new Set();
  
  private pollTimer: any = null;
  private pollInterval: number = 3000;
  private identifyingDomains: Set<string> = new Set();

  constructor() {
    if (typeof window !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (!document.hidden && this.identifyingDomains.size > 0) {
          this.fetchHistory();
        }
      });
    }
  }

  public subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private notify() {
    this.listeners.forEach((l) => l());
  }

  private cachedSnapshot = { history: this.history, watchlist: this.watchlist };

  public getSnapshot = () => {
    if (this.cachedSnapshot.history !== this.history || this.cachedSnapshot.watchlist !== this.watchlist) {
      this.cachedSnapshot = { history: this.history, watchlist: this.watchlist };
    }
    return this.cachedSnapshot;
  };

  public async fetchWatchlist() {
    try {
      this.watchlist = await getWatchlist();
      this.notify();
    } catch {}
  }

  public async addDomain(
    domain: string,
    options?: { source?: "manual" | "client_profile" | "continuous"; label?: string; include_subdomains?: boolean }
  ) {
    await addToWatchlist(domain, options);
    await this.fetchWatchlist();
  }

  public async removeDomain(domain: string) {
    await removeFromWatchlist(domain);
    await this.fetchWatchlist();
  }

  public async toggleWatchlist(
    domain: string,
    options?: { source?: "manual" | "client_profile" | "continuous"; label?: string; include_subdomains?: boolean }
  ) {
    const clean = domain.toLowerCase().trim();
    const isWatched = this.watchlist.some(
      w => (w.domain || "").toLowerCase() === clean || ((w as any).host || "").toLowerCase() === clean
    );
    try {
      if (isWatched) {
        await removeFromWatchlist(domain);
      } else {
        await addToWatchlist(domain, options);
      }
      await this.fetchWatchlist();
    } catch (err) {
      console.error("Failed to toggle watchlist", err);
      throw err;
    }
  }

  public async fetchHistory() {
    try {
      const newHistory = await getDomainHistory();
      
      const newIdentifying = new Set<string>();
      
      newHistory.forEach(item => {
        if (item.status === "identifying") {
          newIdentifying.add(item.domain);
        } else if (item.status === "ready" && this.identifyingDomains.has(item.domain)) {
          // Toast for domain ready
          toast.success(
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } }, 
              React.createElement('div', null, `${item.domain} is ready`),
              React.createElement('button', {
                onClick: () => {
                  const url = new URL(window.location.href);
                  url.searchParams.set("domain", item.domain);
                  window.location.href = url.toString();
                },
                style: { background: "#2d7dd2", color: "white", border: "none", padding: "4px 8px", borderRadius: "4px", fontSize: "12px", cursor: "pointer", width: "fit-content" }
              }, "Open")
            ),
            { duration: 10000, id: `ready-${item.domain}` } // dedupe
          );
        }
      });

      this.history = newHistory;
      this.identifyingDomains = newIdentifying;
      this.notify();

      if (this.identifyingDomains.size > 0 && !document.hidden) {
        this.schedulePoll();
      } else {
        if (this.pollTimer) {
          clearTimeout(this.pollTimer);
          this.pollTimer = null;
        }
        this.pollInterval = 3000;
      }
    } catch {}
  }

  private schedulePoll() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    
    this.pollTimer = setTimeout(() => {
      this.fetchHistory();
      // Backoff
      if (this.pollInterval === 3000) this.pollInterval = 5000;
      else if (this.pollInterval === 5000) this.pollInterval = 10000;
    }, this.pollInterval);
  }

  public resetUserScopedState() {
    this.history = [];
    this.watchlist = [];
    this.identifyingDomains.clear();
    this.pollInterval = 3000;
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    this.notify();
  }
}

export const domainStore = new DomainStore();
